/**
 * EventTemplatesManager - Admin UI for managing event templates
 * 
 * Event templates pre-configure case settings for specific disaster types.
 * When onboarding 300+ cases from the same event (e.g., plant explosion),
 * applying a template saves time by pre-populating common settings.
 */

import React, { useState, useEffect } from 'react';
import {
  FileText,
  Plus,
  Edit2,
  Trash2,
  Clock,
  DollarSign,
  ChevronDown,
  ChevronUp,
  Copy,
  Check,
  AlertTriangle,
  Building2,
  Flame,
  CloudRain,
  Shield,
  Biohazard,
  Lock
} from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

interface ContinuationRates {
  rent: number;
  utilities: number;
  insurance: number;
  payroll: number;
  officer_compensation: number;
  loan_payments: number;
  cogs: number;
  marketing: number;
  professional_fees: number;
  other_fixed: number;
}

interface EventTemplate {
  id: string;
  name: string;
  event_type: string;
  description?: string;
  calculation_mode: string;
  default_closure_months: number;
  continuation_rates: ContinuationRates;
  common_extra_expenses: string[];
  attorney_notes?: string;
  org_id?: string;
  is_active: boolean;
  is_system: boolean;
  created_at: string;
  updated_at: string;
  usage_count: number;
}

const EVENT_TYPE_ICONS: Record<string, React.ElementType> = {
  plant_explosion: Biohazard,
  hurricane: CloudRain,
  fire: Flame,
  civil_authority: Shield,
  pandemic: AlertTriangle,
  contamination: Biohazard,
  default: FileText
};

const CALCULATION_MODES = [
  { value: 'standard_bi', label: 'Standard Business Interruption', description: 'Business stays open but has reduced revenue' },
  { value: 'future_closure', label: 'Future Forced Closure', description: 'Business is closed for extended period' }
];

const CLOSURE_DURATIONS = [
  { value: 3, label: '3 months' },
  { value: 6, label: '6 months' },
  { value: 12, label: '12 months (1 year)' },
  { value: 18, label: '18 months' },
  { value: 24, label: '24 months (2 years)' },
  { value: 36, label: '36 months (3 years)' },
  { value: 48, label: '48 months (4 years)' },
  { value: 60, label: '60 months (5 years)' }
];

export default function EventTemplatesManager() {
  const [templates, setTemplates] = useState<EventTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedTemplate, setExpandedTemplate] = useState<string | null>(null);
  const [editingTemplate, setEditingTemplate] = useState<EventTemplate | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const data = await api.get('/api/v1/event-templates/');
      setTemplates(data);
    } catch (error) {
      console.error('Failed to load templates:', error);
      toast.error('Failed to load event templates');
    } finally {
      setLoading(false);
    }
  };

  const duplicateTemplate = async (template: EventTemplate) => {
    try {
      const newTemplate = {
        name: `${template.name} (Copy)`,
        event_type: template.event_type,
        description: template.description,
        calculation_mode: template.calculation_mode,
        default_closure_months: template.default_closure_months,
        continuation_rates: template.continuation_rates,
        common_extra_expenses: template.common_extra_expenses,
        attorney_notes: template.attorney_notes
      };
      
      await api.post('/api/v1/event-templates/', newTemplate);
      toast.success('Template duplicated');
      loadTemplates();
    } catch (error) {
      console.error('Failed to duplicate template:', error);
      toast.error('Failed to duplicate template');
    }
  };

  const deleteTemplate = async (templateId: string) => {
    if (!confirm('Are you sure you want to deactivate this template?')) return;
    
    try {
      await api.delete(`/api/v1/event-templates/${templateId}`);
      toast.success('Template deactivated');
      loadTemplates();
    } catch (error) {
      console.error('Failed to delete template:', error);
      toast.error('Failed to delete template');
    }
  };

  const toggleExpand = (templateId: string) => {
    setExpandedTemplate(expandedTemplate === templateId ? null : templateId);
  };

  const getIcon = (eventType: string) => {
    const Icon = EVENT_TYPE_ICONS[eventType] || EVENT_TYPE_ICONS.default;
    return <Icon className="w-5 h-5" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: 'var(--color-brand)' }}></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>Event Templates</h2>
          <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
            Pre-configured settings for different disaster types. Apply to cases for quick setup.
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg transition-colors"
          style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
        >
          <Plus className="w-4 h-4" />
          Create Template
        </button>
      </div>

      {/* System Templates Section */}
      <div className="space-y-3">
        <h3 className="text-sm font-medium flex items-center gap-2" style={{ color: 'var(--color-text-secondary)' }}>
          <Lock className="w-4 h-4" />
          System Templates (Built-in)
        </h3>
        <div className="grid gap-4">
          {templates.filter(t => t.is_system).map(template => (
            <TemplateCard
              key={template.id}
              template={template}
              expanded={expandedTemplate === template.id}
              onToggle={() => toggleExpand(template.id)}
              onDuplicate={() => duplicateTemplate(template)}
              onDelete={() => {}}
              getIcon={getIcon}
            />
          ))}
        </div>
      </div>

      {/* Custom Templates Section */}
      {templates.some(t => !t.is_system) && (
        <div className="space-y-3">
          <h3 className="text-sm font-medium flex items-center gap-2" style={{ color: 'var(--color-text-secondary)' }}>
            <Building2 className="w-4 h-4" />
            Custom Templates
          </h3>
          <div className="grid gap-4">
            {templates.filter(t => !t.is_system).map(template => (
              <TemplateCard
                key={template.id}
                template={template}
                expanded={expandedTemplate === template.id}
                onToggle={() => toggleExpand(template.id)}
                onDuplicate={() => duplicateTemplate(template)}
                onDelete={() => deleteTemplate(template.id)}
                onEdit={() => setEditingTemplate(template)}
                getIcon={getIcon}
              />
            ))}
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {(showCreateModal || editingTemplate) && (
        <TemplateModal
          template={editingTemplate}
          onClose={() => {
            setShowCreateModal(false);
            setEditingTemplate(null);
          }}
          onSave={() => {
            setShowCreateModal(false);
            setEditingTemplate(null);
            loadTemplates();
          }}
        />
      )}
    </div>
  );
}


// Template Card Component
interface TemplateCardProps {
  template: EventTemplate;
  expanded: boolean;
  onToggle: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onEdit?: () => void;
  getIcon: (eventType: string) => React.ReactElement;
}

function TemplateCard({ template, expanded, onToggle, onDuplicate, onDelete, onEdit, getIcon }: TemplateCardProps) {
  return (
    <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: template.is_system ? 'var(--color-needs-human-tint-bd)' : 'var(--color-border)' }}>
      {/* Card Header */}
      <div
        className="p-4 cursor-pointer transition-colors"
        style={{ backgroundColor: 'var(--color-bg-surface)' }}
        onClick={onToggle}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg" style={template.is_system ? { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' } : { backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}>
              {getIcon(template.event_type)}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h4 className="font-medium" style={{ color: 'var(--color-text-primary)' }}>{template.name}</h4>
                {template.is_system && (
                  <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>System</span>
                )}
              </div>
              <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                {template.calculation_mode === 'future_closure' ? 'Forced Closure' : 'Business Interruption'} • {template.default_closure_months} months default
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!template.is_system && onEdit && (
              <button
                onClick={(e) => { e.stopPropagation(); onEdit(); }}
                className="p-2 rounded-lg transition-colors"
                style={{ color: 'var(--color-text-dim)' }}
                title="Edit template"
              >
                <Edit2 className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); onDuplicate(); }}
              className="p-2 rounded-lg transition-colors"
              style={{ color: 'var(--color-text-dim)' }}
              title="Duplicate as custom template"
            >
              <Copy className="w-4 h-4" />
            </button>
            {!template.is_system && (
              <button
                onClick={(e) => { e.stopPropagation(); onDelete(); }}
                className="p-2 rounded-lg transition-colors"
                style={{ color: 'var(--color-text-dim)' }}
                title="Deactivate template"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
            {expanded ? (
              <ChevronUp className="w-5 h-5" style={{ color: 'var(--color-text-dim)' }} />
            ) : (
              <ChevronDown className="w-5 h-5" style={{ color: 'var(--color-text-dim)' }} />
            )}
          </div>
        </div>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="border-t p-4 space-y-4" style={{ borderColor: 'var(--color-border-sub)', backgroundColor: 'var(--color-bg-base)' }}>
          {template.description && (
            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{template.description}</p>
          )}

          {/* Continuation Rates */}
          <div>
            <h5 className="text-sm font-medium mb-2 flex items-center gap-2" style={{ color: 'var(--color-text-secondary)' }}>
              <DollarSign className="w-4 h-4" />
              Default Continuation Rates
            </h5>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {Object.entries(template.continuation_rates || {}).map(([key, value]) => (
                <div key={key} className="px-3 py-2 rounded-lg border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
                  <div className="text-xs capitalize" style={{ color: 'var(--color-text-muted)' }}>{key.replace(/_/g, ' ')}</div>
                  <div className="font-medium" style={{ color: 'var(--color-text-primary)' }}>{value}%</div>
                </div>
              ))}
            </div>
          </div>

          {/* Extra Expenses */}
          {template.common_extra_expenses?.length > 0 && (
            <div>
              <h5 className="text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>Common Extra Expenses</h5>
              <div className="flex flex-wrap gap-2">
                {template.common_extra_expenses.map((expense, i) => (
                  <span key={i} className="text-sm px-3 py-1 border rounded-full" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)', color: 'var(--color-text-secondary)' }}>
                    {expense}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Attorney Notes */}
          {template.attorney_notes && (
            <div className="rounded-lg p-3" style={{ backgroundColor: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
              <h5 className="text-sm font-medium mb-1" style={{ color: 'var(--color-needs-human-text)' }}>Attorney Notes</h5>
              <p className="text-sm" style={{ color: 'var(--color-needs-human-text)' }}>{template.attorney_notes}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// Create/Edit Modal
interface TemplateModalProps {
  template: EventTemplate | null;
  onClose: () => void;
  onSave: () => void;
}

function TemplateModal({ template, onClose, onSave }: TemplateModalProps) {
  const [formData, setFormData] = useState({
    name: template?.name || '',
    event_type: template?.event_type || 'custom',
    description: template?.description || '',
    calculation_mode: template?.calculation_mode || 'future_closure',
    default_closure_months: template?.default_closure_months || 24,
    continuation_rates: template?.continuation_rates || {
      rent: 100,
      utilities: 40,
      insurance: 100,
      payroll: 70,
      officer_compensation: 80,
      loan_payments: 100,
      cogs: 10,
      marketing: 20,
      professional_fees: 50,
      other_fixed: 50
    },
    common_extra_expenses: template?.common_extra_expenses || [],
    attorney_notes: template?.attorney_notes || ''
  });
  const [saving, setSaving] = useState(false);
  const [newExpense, setNewExpense] = useState('');

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error('Template name is required');
      return;
    }

    try {
      setSaving(true);
      if (template) {
        await api.put(`/api/v1/event-templates/${template.id}`, formData);
        toast.success('Template updated');
      } else {
        await api.post('/api/v1/event-templates/', formData);
        toast.success('Template created');
      }
      onSave();
    } catch (error) {
      console.error('Failed to save template:', error);
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const updateRate = (key: string, value: number) => {
    setFormData({
      ...formData,
      continuation_rates: {
        ...formData.continuation_rates,
        [key]: Math.min(100, Math.max(0, value))
      }
    });
  };

  const addExpense = () => {
    if (newExpense.trim() && !formData.common_extra_expenses.includes(newExpense.trim())) {
      setFormData({
        ...formData,
        common_extra_expenses: [...formData.common_extra_expenses, newExpense.trim()]
      });
      setNewExpense('');
    }
  };

  const removeExpense = (expense: string) => {
    setFormData({
      ...formData,
      common_extra_expenses: formData.common_extra_expenses.filter(e => e !== expense)
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
        <div className="p-6 border-b" style={{ borderColor: 'var(--color-border)' }}>
          <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            {template ? 'Edit Template' : 'Create Event Template'}
          </h3>
        </div>

        <div className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>Template Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2"
                style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)', color: 'var(--color-text-primary)' }}
                placeholder="e.g., Plant Explosion - Zone A"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>Event Type</label>
              <input
                type="text"
                value={formData.event_type}
                onChange={(e) => setFormData({ ...formData, event_type: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2"
                style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)', color: 'var(--color-text-primary)' }}
                placeholder="e.g., plant_explosion"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>Default Closure Duration</label>
              <select
                value={formData.default_closure_months}
                onChange={(e) => setFormData({ ...formData, default_closure_months: parseInt(e.target.value) })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2"
                style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)', color: 'var(--color-text-primary)' }}
              >
                {CLOSURE_DURATIONS.map(d => (
                  <option key={d.value} value={d.value}>{d.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Calculation Mode */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>Calculation Mode</label>
            <div className="grid grid-cols-2 gap-4">
              {CALCULATION_MODES.map(mode => (
                <button
                  key={mode.value}
                  type="button"
                  onClick={() => setFormData({ ...formData, calculation_mode: mode.value })}
                  className="p-4 text-left rounded-lg border-2 transition-colors"
                  style={
                    formData.calculation_mode === mode.value
                      ? { borderColor: 'var(--color-brand)', backgroundColor: 'var(--color-needs-human-tint)' }
                      : { borderColor: 'var(--color-border)' }
                  }
                >
                  <div className="font-medium" style={{ color: 'var(--color-text-primary)' }}>{mode.label}</div>
                  <div className="text-sm" style={{ color: 'var(--color-text-muted)' }}>{mode.description}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2"
              style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)', color: 'var(--color-text-primary)' }}
              rows={2}
              placeholder="Describe when this template should be used..."
            />
          </div>

          {/* Continuation Rates */}
          <div>
            <label className="block text-sm font-medium mb-3" style={{ color: 'var(--color-text-secondary)' }}>
              Default Continuation Rates (%)
            </label>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {Object.entries(formData.continuation_rates).map(([key, value]) => (
                <div key={key}>
                  <label className="block text-xs mb-1 capitalize" style={{ color: 'var(--color-text-muted)' }}>
                    {key.replace(/_/g, ' ')}
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={value}
                    onChange={(e) => updateRate(key, parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2"
                    style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)', color: 'var(--color-text-primary)' }}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Extra Expenses */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>Common Extra Expenses</label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={newExpense}
                onChange={(e) => setNewExpense(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addExpense()}
                className="flex-1 px-4 py-2 border rounded-lg focus:ring-2"
                style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)', color: 'var(--color-text-primary)' }}
                placeholder="Add expense category..."
              />
              <button
                type="button"
                onClick={addExpense}
                className="px-4 py-2 rounded-lg transition-colors"
                style={{ backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
              >
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.common_extra_expenses.map((expense, i) => (
                <span key={i} className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                  {expense}
                  <button
                    type="button"
                    onClick={() => removeExpense(expense)}
                    style={{ color: 'var(--color-text-dim)' }}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Attorney Notes */}
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>Attorney Notes</label>
            <textarea
              value={formData.attorney_notes}
              onChange={(e) => setFormData({ ...formData, attorney_notes: e.target.value })}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2"
              style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)', color: 'var(--color-text-primary)' }}
              rows={3}
              placeholder="Tips and considerations for attorneys using this template..."
            />
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t flex justify-end gap-3" style={{ borderColor: 'var(--color-border)' }}>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg transition-colors"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving}
            className="px-6 py-2 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
            style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2" style={{ borderColor: 'var(--color-on-brand)' }}></div>
                Saving...
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                {template ? 'Update Template' : 'Create Template'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
